/** Shared, browser-safe constants and helpers for SalonTurbo. */

// 1 Coin = 25,00 € netto (2.500 Cent), zzgl. MwSt. Pro Boost-Tag wird 1 Coin abgebucht.
export const COIN_PRICE_CENTS = 2500;
export const VAT_RATE = 0.19;
export const MIN_COINS = 4;
export const MAX_BOOST_DAYS_AHEAD = 30;
export const DAILY_BUDGET_EUR = 20;
export const ADS_END_BUFFER_HOURS = 2;
export const BERLIN_TZ = "Europe/Berlin";
export const SUPPORT_EMAIL = "david@swax-media.de";
/** Öffentlich sichtbare Kontaktadresse in Mails und im Footer. */
export const PUBLIC_CONTACT_EMAIL = "hallo@salonturbo.de";

export const WEEKDAYS = [
  { value: 1, short: "Mo", label: "Montag" },
  { value: 2, short: "Di", label: "Dienstag" },
  { value: 3, short: "Mi", label: "Mittwoch" },
  { value: 4, short: "Do", label: "Donnerstag" },
  { value: 5, short: "Fr", label: "Freitag" },
  { value: 6, short: "Sa", label: "Samstag" },
  { value: 0, short: "So", label: "Sonntag" },
] as const;

export function formatEuro(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

/** Bruttobetrag (inkl. deutscher MwSt.) aus einem Nettobetrag in Cent. */
export function grossFromNet(netCents: number, rate: number = VAT_RATE): number {
  return Math.round(netCents * (1 + rate));
}

export function formatMicros(micros: number): string {
  return formatEuro(Math.round(micros / 10000));
}


/** ISO date (yyyy-mm-dd) for "today" in Berlin, independent of the runtime timezone. */
export function berlinToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BERLIN_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function isoWeekday(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1)).getUTCDay();
}

export function formatDateLong(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1)));
}

export function formatDateShort(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1)));
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

/** Urgency keyword set used for the Google Ads campaign of a salon. */
export function urgencyKeywords(city: string | null | undefined): string[] {
  const town = (city ?? "").trim();
  const base = [
    "friseur termin heute",
    "friseur heute noch termin frei",
    "friseur spontan termin",
    "friseur kurzfristig termin",
    "friseur ohne termin",
    "haare schneiden heute",
  ];
  if (!town) return base;
  return [
    ...base.map((k) => `${k} ${town.toLowerCase()}`),
    `friseur ${town.toLowerCase()} heute`,
    `friseur ${town.toLowerCase()} kurzfristig`,
  ];
}

/** Kürzt Text hart auf die von Google erlaubte Länge. */
export function clampAdText(text: string, max: number): string {
  const value = text.trim();
  return value.length <= max ? value : `${value.slice(0, max - 1).trim()}…`;
}

export type AdsSalonInput = {
  name: string;
  slug: string;
  city: string | null;
  phone: string | null;
  street?: string | null;
  booking_url?: string | null;
};

/** Anzeigentitel (max. 30 Zeichen) und Beschreibungen (max. 90 Zeichen) der RSA. */
export function buildAdCopy(salon: AdsSalonInput): {
  headlines: string[];
  descriptions: string[];
} {
  const city = salon.city?.trim() ?? "";
  const nameHeadline = clampAdText(`Friseur ${salon.name}`, 30);
  const cityHeadline = clampAdText(`Friseur ${city || "in der Nähe"}`, 30);
  const headlines = [
    nameHeadline,
    "Heute noch Termine frei",
    cityHeadline,
    "Jetzt anrufen & Termin holen",
    clampAdText(salon.name, 30),
    "Kurzfristig zum Friseur",
    city ? clampAdText(`Friseur ${city} ohne Termin`, 30) : "Friseur ohne Termin",
    "Spontan Termin bekommen",
    "Freie Stühle heute",
  ];
  const descriptions = [
    clampAdText(
      `Spontan freie Termine bei ${salon.name}${city ? ` in ${city}` : ""}. Direkt anrufen und heute noch dran sein.`,
      90,
    ),
    "Kurzfristige Termine, freundliches Team, faire Preise. Jetzt Termin sichern.",
    city
      ? clampAdText(`Friseur in ${city}: heute noch freie Termine. Ein Anruf genügt.`, 90)
      : "Freie Termine noch heute. Ein Anruf genügt – wir bestätigen sofort.",
  ];
  return { headlines: dedupe(headlines), descriptions: dedupe(descriptions) };
}

function dedupe(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.length > 0))];
}

export type AdSitelink = { text: string; description1: string; description2: string; path: string };

/** Sitelink-Erweiterungen (Text max. 25, Beschreibungen max. 35 Zeichen). */
export function buildSitelinks(salon: AdsSalonInput): AdSitelink[] {
  const links: AdSitelink[] = [
    {
      text: "Termin für heute",
      description1: "Freie Termine kurzfristig",
      description2: "Direkt anrufen",
      path: "#kontakt",
    },
    {
      text: "Öffnungszeiten",
      description1: "Wann wir heute geöffnet haben",
      description2: "Alle Zeiten im Überblick",
      path: "#oeffnungszeiten",
    },
    {
      text: "Unser Salon",
      description1: "Team, Stil und Atmosphäre",
      description2: "Bilder ansehen",
      path: "",
    },
    {
      text: "Anfahrt & Adresse",
      description1: salon.city ? clampAdText(`Zentral in ${salon.city}`, 35) : "So findest du uns",
      description2: "Route direkt starten",
      path: "#kontakt",
    },
  ];
  if (salon.booking_url) {
    links[0] = {
      text: "Termin buchen",
      description1: "Online oder telefonisch",
      description2: "Heute noch möglich",
      path: "#kontakt",
    };
  }
  return links.map((link) => ({
    ...link,
    text: clampAdText(link.text, 25),
    description1: clampAdText(link.description1, 35),
    description2: clampAdText(link.description2, 35),
  }));
}

/** Zusatzinformationen (Callouts, max. 25 Zeichen). */
export function buildCallouts(salon: AdsSalonInput): string[] {
  const city = salon.city?.trim() ?? "";
  return dedupe(
    [
      "Kurzfristige Termine",
      "Termin per Anruf",
      city ? clampAdText(`Zentral in ${city}`, 25) : "",
      "Persönliche Beratung",
      salon.booking_url ? "Online buchbar" : "",
    ].map((text) => clampAdText(text, 25)),
  );
}

/** Strukturierte Snippets: Header "Dienstleistungen" (Werte max. 25 Zeichen). */
export function buildSnippetValues(): string[] {
  return ["Haarschnitt", "Färben & Strähnen", "Styling", "Beratung", "Bart & Kontur"];
}


export type OpeningHourRow = {
  weekday: number;
  is_closed: boolean;
  open_time: string | null;
  close_time: string | null;
};

/** "18:00:00" minus 2h -> "16:00". Returns null when the salon is closed. */
export function adsEndTime(row: OpeningHourRow | undefined): string | null {
  if (!row || row.is_closed || !row.close_time) return null;
  const [h, m] = row.close_time.split(":").map(Number);
  const minutes = (h ?? 0) * 60 + (m ?? 0) - ADS_END_BUFFER_HOURS * 60;
  if (minutes <= 0) return null;
  const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

/** Aktuelle Berliner Uhrzeit als "HH:MM". */
export function berlinNowTime(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("de-DE", {
    timeZone: BERLIN_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);
}

/**
 * Frühester buchbarer Tag. Am Tag des Onboarding-Abschlusses ist die
 * Google-Kampagne noch in der Prüfung (bis zu 24 h) – dann startet das
 * Boost-Fenster erst morgen.
 */
export function earliestBoostDate(
  onboardingCompletedAt?: string | null,
  now: Date = new Date(),
): string {
  const today = berlinToday(now);
  if (!onboardingCompletedAt) return today;
  const completedDay = berlinToday(new Date(onboardingCompletedAt));
  return completedDay >= today ? addDaysIso(today, 1) : today;
}

/**
 * Ein Tag ist boostbar, wenn der Salon offen ist, der Tag nicht vor dem
 * frühesten buchbaren Tag liegt und – bei "heute" – die Ads-Endzeit
 * (2h vor Schließung) noch nicht erreicht ist.
 */
export function isDateBoostable(
  iso: string,
  hours: OpeningHourRow[],
  now: Date = new Date(),
  earliest?: string,
): boolean {
  if (earliest && iso < earliest) return false;
  const row = hours.find((hour) => hour.weekday === isoWeekday(iso));
  const end = adsEndTime(row);
  if (!end) return false;
  if (iso !== berlinToday(now)) return true;
  return berlinNowTime(now) < end;
}

/** Nächstes buchbares Datum innerhalb des Boost-Fensters, sonst null. */
export function nextBoostableDate(
  hours: OpeningHourRow[],
  taken: Record<string, string> = {},
  now: Date = new Date(),
  earliest?: string,
): string | null {
  const today = berlinToday(now);
  for (let index = 0; index <= MAX_BOOST_DAYS_AHEAD; index += 1) {
    const date = addDaysIso(today, index);
    const status = taken[date];
    if (status && status !== "cancelled") continue;
    if (isDateBoostable(date, hours, now, earliest)) return date;
  }
  return null;
}

